import type { AppDispatch } from "_shared/_stores/store-types";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { bookmarksApi } from "marketplace/bookmarks/_api/bookmark-api";
import {
  getTeamBookmarkByTeamId,
  getHasTeamBookmarkStatusFailed,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import type { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

/** The most ids that `POST marketplace/team-bookmarks/by-team-ids` accepts in one request. */
export const TEAM_BOOKMARK_STATUS_BATCH_SIZE = 100;

const pendingTeamIds = new Set<string>();
const requestedTeamIds = new Set<string>();
let isFlushScheduled = false;

/**
 * Asks the server whether the session user has bookmarked this team. The ids that components
 * ask for in the same tick go in one bulk request: a marketplace results page shows a bookmark
 * button on each card, and one request for each card would be an N+1 across the network.
 * An id is asked once until its request ends; the answer goes into the bookmarks slice.
 */
export function requestTeamBookmarkStatus(dispatch: AppDispatch, teamId: Guid): void {
  if (pendingTeamIds.has(teamId.value) || requestedTeamIds.has(teamId.value)) return;
  pendingTeamIds.add(teamId.value);
  if (isFlushScheduled) return;
  isFlushScheduled = true;
  setTimeout(() => flushTeamBookmarkStatusRequests(dispatch), 0);
}

function flushTeamBookmarkStatusRequests(dispatch: AppDispatch): void {
  isFlushScheduled = false;
  const teamIds = [...pendingTeamIds];
  pendingTeamIds.clear();

  for (let start = 0; start < teamIds.length; start += TEAM_BOOKMARK_STATUS_BATCH_SIZE) {
    const batch = teamIds.slice(start, start + TEAM_BOOKMARK_STATUS_BATCH_SIZE);
    for (const teamId of batch) requestedTeamIds.add(teamId);
    const request = dispatch(
      bookmarksApi.endpoints.getTeamBookmarksByTeamIds.initiate(
        { teamIds: batch.map((teamId) => new Guid(teamId)) },
        // Asked only when the slice has no answer, so a cached reply is never the one wanted. RTK
        // Query keeps the last reply for these ids a minute after the request ends, and without
        // this would return it unsent.
        { forceRefetch: true },
      ),
    );
    void request.finally(() => {
      for (const teamId of batch) requestedTeamIds.delete(teamId);
      request.unsubscribe();
    });
  }
}

export type TeamBookmarkStatus = {
  /** The bookmark, null when there is none, or undefined while the answer loads or after it failed. */
  bookmark: BookmarkedTeamJSON | null | undefined;
  /** The ask failed. It is not repeated by itself, so that an outage is not asked about in a loop. */
  hasFailed: boolean;
  /** Asks again after a failure. */
  retry: () => void;
};

/**
 * The session user's bookmark of this team. Pass no id, or `skip`, to ask nothing.
 */
export default function useTeamBookmarkStatus(
  teamId?: Guid,
  options?: { skip?: boolean },
): TeamBookmarkStatus {
  const dispatch = useAttorneyHubDispatch();
  const bookmark = useSelector(getTeamBookmarkByTeamId(teamId));
  const failed = useSelector(getHasTeamBookmarkStatusFailed(teamId));
  const skip = options?.skip ?? false;
  const teamIdValue = teamId?.value;

  useEffect(() => {
    if (skip || !teamIdValue || bookmark !== undefined || failed) return;
    requestTeamBookmarkStatus(dispatch, new Guid(teamIdValue));
  }, [bookmark, dispatch, failed, teamIdValue, skip]);

  const retry = useCallback(() => {
    if (skip || !teamIdValue) return;
    requestTeamBookmarkStatus(dispatch, new Guid(teamIdValue));
  }, [dispatch, teamIdValue, skip]);

  if (skip) return { bookmark: undefined, hasFailed: false, retry };
  return { bookmark, hasFailed: failed, retry };
}
